import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { bodyProfileInputSchema } from '@loggy/shared';
import type { ActivityLevel, BodyProfile } from '@loggy/shared';

import { Alert, Button, Card, Field, Heading, Inline, Select, Spinner, Stack, Text, Input } from '../design/index.js';
import { deleteBodyProfile, fetchBodyProfile, saveBodyProfile } from '../api.js';
import { ACTIVITY_LEVEL_OPTIONS, fieldErrorsFromZodError, type BodyProfileFieldErrors } from './bodyProfileFields.js';
import styles from './BodyProfileForm.module.css';

type Phase =
  | { kind: 'loading' }
  | { kind: 'load-error'; message: string }
  | { kind: 'ready' }
  | { kind: 'saving' }
  | { kind: 'remove-confirm' }
  | { kind: 'removing' };

/**
 * No pre-selection, deliberately.
 *
 * This started as a default of 'female', which is one click from either
 * answer and therefore looks harmless. It is not: the two forms of the
 * equation differ by 166 kcal/day, so somebody who fills in their height and
 * birth date without noticing a dropdown that already looks answered gets a
 * silently wrong expenditure — and every figure on the weight page is built
 * on it. The app has no business guessing this one, so it asks.
 */
type SexChoice = BodyProfile['sex'] | '';
const EMPTY_ACTIVITY: ActivityLevel = 'moderate';

/**
 * The optional body profile that feeds the Mifflin–St Jeor estimate behind a
 * weight goal's healthy band.
 *
 * Entirely optional, and the form says so before a single field is filled
 * in: skipping it costs one band on one chart, nothing else. `sex` is asked
 * as what it actually is — a term the equation picks a constant from — not
 * a question the app has an opinion about.
 */
export function BodyProfileForm({ className }: { className?: string }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [hasProfile, setHasProfile] = useState(false);

  const [sex, setSex] = useState<SexChoice>('');
  const [birthDate, setBirthDate] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [activityLevel, setActivityLevel] = useState<ActivityLevel>(EMPTY_ACTIVITY);

  const [fieldErrors, setFieldErrors] = useState<BodyProfileFieldErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setPhase({ kind: 'loading' });
    try {
      const profile = await fetchBodyProfile();
      if (profile) {
        setSex(profile.sex);
        setBirthDate(profile.birthDate);
        setHeightCm(String(profile.heightCm));
        setActivityLevel(profile.activityLevel);
        setHasProfile(true);
      } else {
        setHasProfile(false);
      }
      setPhase({ kind: 'ready' });
    } catch (err) {
      setPhase({
        kind: 'load-error',
        message: err instanceof Error ? err.message : 'Could not load your body profile.',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const busy = phase.kind === 'saving' || phase.kind === 'removing';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    // heightCm travels as a string in local state so the input can hold an
    // empty box or a partial number; it only becomes a number right here,
    // at the boundary the schema actually validates.
    const candidate = {
      sex,
      birthDate,
      heightCm: heightCm.trim() === '' ? Number.NaN : Number(heightCm),
      activityLevel,
    };
    const result = bodyProfileInputSchema.safeParse(candidate);
    if (!result.success) {
      setFieldErrors(fieldErrorsFromZodError(result.error));
      setSaveError(null);
      return;
    }

    setFieldErrors({});
    setSaveError(null);
    setPhase({ kind: 'saving' });
    try {
      await saveBodyProfile(result.data);
      setHasProfile(true);
      setPhase({ kind: 'ready' });
    } catch (err) {
      setPhase({ kind: 'ready' });
      setSaveError(err instanceof Error ? err.message : 'Could not save your body profile.');
    }
  }

  async function handleRemove() {
    setPhase({ kind: 'removing' });
    setRemoveError(null);
    try {
      await deleteBodyProfile();
      setSex('');
      setBirthDate('');
      setHeightCm('');
      setActivityLevel(EMPTY_ACTIVITY);
      setHasProfile(false);
      setFieldErrors({});
      setPhase({ kind: 'ready' });
    } catch (err) {
      setPhase({ kind: 'remove-confirm' });
      setRemoveError(err instanceof Error ? err.message : 'Could not remove your body profile.');
    }
  }

  if (phase.kind === 'loading') {
    return (
      <Stack gap={4} className={className}>
        <Text size="xs" color="muted" className={styles.label}>Body profile</Text>
        <Card data-testid="body-profile-loading">
          <Inline gap={3} align="center">
            <Spinner size="sm" aria-label="Loading your body profile" />
            <Text size="sm" color="muted">Loading…</Text>
          </Inline>
        </Card>
      </Stack>
    );
  }

  if (phase.kind === 'load-error') {
    return (
      <Stack gap={4} className={className}>
        <Text size="xs" color="muted" className={styles.label}>Body profile</Text>
        <Card data-testid="body-profile-load-error">
          <Stack gap={3}>
            <Alert variant="error" title="Couldn't load your body profile">
              {phase.message}
            </Alert>
            <Button variant="secondary" size="sm" onClick={() => void load()}>
              Try again
            </Button>
          </Stack>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap={4} className={className}>
      <Text size="xs" color="muted" className={styles.label}>Body profile</Text>
      <Card data-testid="body-profile-form">
        <form onSubmit={handleSubmit} noValidate>
          <Stack gap={5}>
            <Stack gap={2}>
              <Heading level={3} size="lg">Body profile</Heading>
              <Text size="sm" color="secondary">
                Optional. It feeds an estimate of your daily energy use, which draws one
                healthy-pace band on a weight goal&rsquo;s chart. Skip it and everything
                else works exactly the same — that one band is the only thing you lose.
              </Text>
            </Stack>

            <Field
              label="Sex"
              required
              error={fieldErrors.sex}
              hint={
                fieldErrors.sex
                  ? undefined
                  : 'A term in the Mifflin–St Jeor equation, which uses a different constant for each — not a judgement about you.'
              }
            >
              <Select
                data-testid="body-profile-sex"
                value={sex}
                onChange={(event) => setSex(event.target.value as SexChoice)}
                disabled={busy}
                required
              >
                <option value="" disabled>Choose one</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </Select>
            </Field>

            <Field
              label="Birth date"
              required
              error={fieldErrors.birthDate}
              hint={fieldErrors.birthDate ? undefined : 'Used only to work out your age for the same equation.'}
            >
              <Input
                data-testid="body-profile-birth-date"
                type="date"
                value={birthDate}
                onChange={(event) => setBirthDate(event.target.value)}
                disabled={busy}
                required
              />
            </Field>

            <Field
              label="Height"
              required
              error={fieldErrors.heightCm}
              hint={fieldErrors.heightCm ? undefined : 'In centimetres, between 80 and 260.'}
            >
              <Input
                data-testid="body-profile-height"
                type="number"
                inputMode="decimal"
                min={80}
                max={260}
                value={heightCm}
                onChange={(event) => setHeightCm(event.target.value)}
                disabled={busy}
                required
              />
            </Field>

            <Field label="Activity level" required>
              <Select
                data-testid="body-profile-activity"
                value={activityLevel}
                onChange={(event) => setActivityLevel(event.target.value as ActivityLevel)}
                disabled={busy}
                required
              >
                {ACTIVITY_LEVEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            {saveError && (
              <Alert variant="error" title="Couldn't save this" data-testid="body-profile-save-error">
                {saveError}
              </Alert>
            )}

            <Inline gap={3} align="center" wrap>
              <Button type="submit" size="sm" data-testid="body-profile-submit" disabled={busy}>
                {phase.kind === 'saving' ? 'Saving…' : hasProfile ? 'Save changes' : 'Save'}
              </Button>
              {phase.kind === 'saving' && (
                <Spinner size="sm" aria-label="Saving your body profile" data-testid="body-profile-saving" />
              )}
            </Inline>

            {hasProfile && (
              <div className={styles.removeZone}>
                {phase.kind !== 'remove-confirm' && phase.kind !== 'removing' ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="body-profile-remove-button"
                    onClick={() => setPhase({ kind: 'remove-confirm' })}
                  >
                    Remove
                  </Button>
                ) : (
                  <Inline gap={3} align="center" wrap data-testid="body-profile-remove-confirm">
                    <Text size="sm" color="secondary">
                      Remove your body profile? Your weight chart loses its healthy-pace
                      band, and you&rsquo;d need to type these details back in by hand to
                      get it again.
                    </Text>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      data-testid="body-profile-remove-confirm-button"
                      onClick={() => void handleRemove()}
                      disabled={phase.kind === 'removing'}
                    >
                      {phase.kind === 'removing' ? 'Removing…' : 'Yes, remove'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={phase.kind === 'removing'}
                      onClick={() => setPhase({ kind: 'ready' })}
                    >
                      Cancel
                    </Button>
                  </Inline>
                )}
                {removeError && (
                  <Alert variant="error" title="Couldn't remove this" data-testid="body-profile-remove-error">
                    {removeError}
                  </Alert>
                )}
              </div>
            )}
          </Stack>
        </form>
      </Card>
    </Stack>
  );
}
